/**
 * Allocation Input Field with Percent/Dollar Toggle
 * Shows both percentage and dollar values with auto-conversion
 */
import React from 'react';
import PropTypes from 'prop-types';
import { DollarSign, Percent } from 'lucide-react';
import HelpTooltip from '../common/HelpTooltip';

const AllocationInputField = ({
  label,
  dollarValue,
  onDollarChange,
  percentValue,
  onPercentChange,
  totalAmount,
  inputMode,
  onToggleMode,
  placeholder = "0",
  className = "",
  helpText = ""
}) => {
  const formatNetWorthValue = (value) => {
    if (!value || value === '') return '';
    const num = parseFloat(String(value).replace(/,/g, ''));
    if (isNaN(num)) return '';
    return num.toLocaleString('en-US', { maximumFractionDigits: 0 });
  };

  const formatCurrency = (value) => {
    const num = parseFloat(value) || 0;
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(num);
  };

  // Calculate the other value based on input mode
  const calculatedPercent = totalAmount > 0 ? ((parseFloat(dollarValue) || 0) / totalAmount * 100).toFixed(1) : '0';
  const calculatedDollar = ((parseFloat(percentValue) || 0) / 100) * (parseFloat(totalAmount) || 0);

  return (
    <div className={`${className}`}>
      <label className="block text-xs font-semibold text-gray-900 mb-1 flex items-center gap-1">
        {label}
        {helpText && <HelpTooltip content={helpText} />}
      </label>
      <div className="relative">
        {/* Toggle Button */}
        <button
          type="button"
          onClick={onToggleMode}
          className="absolute left-2 top-1/2 -translate-y-1/2 p-1 hover:bg-gray-200 rounded transition"
          title={`Switch to ${inputMode === 'dollar' ? 'percentage' : 'dollar'} input`}
        >
          {inputMode === 'dollar' ? (
            <DollarSign size={16} className="text-blue-600" />
          ) : (
            <Percent size={16} className="text-blue-600" />
          )}
        </button>

        {/* Input Field */}
        <input
          type="text"
          value={inputMode === 'dollar' ? formatNetWorthValue(dollarValue) : percentValue}
          onChange={(e) => {
            const value = e.target.value.replace(/,/g, '');
            // Allow empty string, numbers, and partial decimal inputs (like "30." or ".5")
            if (value === '' || value === '.' || /^-?\d*\.?\d*$/.test(value)) {
              if (inputMode === 'dollar') {
                onDollarChange(value);
              } else {
                // In percent mode, update the percent state
                onPercentChange(value);
                // Also calculate and update the dollar value
                const newDollar = ((parseFloat(value) || 0) / 100) * (parseFloat(totalAmount) || 0);
                onDollarChange(String(newDollar));
              }
            }
          }}
          placeholder={placeholder}
          className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />

        {/* Calculated Value Display */}
        <div className="mt-1 text-xs text-gray-600">
          {inputMode === 'dollar' ? (
            <span>{calculatedPercent}%</span>
          ) : (
            <span>{formatCurrency(calculatedDollar)}</span>
          )}
        </div>
      </div>
    </div>
  );
};

AllocationInputField.propTypes = {
  label: PropTypes.string.isRequired,
  dollarValue: PropTypes.string.isRequired,
  onDollarChange: PropTypes.func.isRequired,
  percentValue: PropTypes.string,
  onPercentChange: PropTypes.func.isRequired,
  totalAmount: PropTypes.string.isRequired,
  inputMode: PropTypes.oneOf(['percent', 'dollar']).isRequired,
  onToggleMode: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  className: PropTypes.string,
  helpText: PropTypes.string
};

export default AllocationInputField;
